import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  background: linear-gradient(135deg, #0a0f1f 0%, #0f1b33 100%);
`;

const Card = styled.div`
  background: rgba(14, 16, 26, 0.9);
  border-radius: 28px;
  width: 100%;
  max-width: 460px;
  padding: 2.5rem;
  box-shadow: 0 30px 90px -40px rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  color: #f5f8ff;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: clamp(1.8rem, 4vw, 2.2rem);
  letter-spacing: -0.02em;
`;

const SubTitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: rgba(245, 248, 255, 0.7);
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: #f5f8ff;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #f5f8ff;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: rgba(90, 200, 255, 0.65);
    background: rgba(6, 9, 18, 0.8);
    box-shadow: 0 0 18px rgba(90, 200, 255, 0.2);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 6px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 38px rgba(0, 242, 254, 0.35);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Message = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${props => (props.$type === 'error' ? 'rgba(255, 138, 128, 0.45)' : 'rgba(74, 222, 128, 0.45)')};
  background: ${props => (props.$type === 'error' ? 'rgba(255, 99, 132, 0.12)' : 'rgba(74, 222, 128, 0.12)')};
  color: ${props => (props.$type === 'error' ? '#ff9aa2' : '#54f1a8')};
  font-size: 0.95rem;
`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = query.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(true);
  const [validating, setValidating] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestError, setRequestError] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }
      setValidating(true);
      try {
        await authAPI.validateResetToken(token);
        setTokenValid(true);
        setError('');
      } catch (err) {
        setTokenValid(false);
        setError(err.message || 'Reset link is invalid or expired.');
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!token) {
      setError('Reset token is missing or invalid.');
      return;
    }

    if (!tokenValid) {
      setError('Reset link is invalid or expired.');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setMessage('Password updated successfully. You can now sign in with your new password.');
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      setError(err.message || 'Unable to reset password. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setRequestMessage('');
    setRequestError('');

    if (!requestEmail.trim()) {
      setRequestError('Please enter your email.');
      return;
    }

    setRequestLoading(true);
    try {
      await authAPI.requestPasswordReset(requestEmail.trim());
      setRequestMessage('If an account exists, a reset link has been sent to your email.');
    } catch (err) {
      setRequestError(err.message || 'Unable to process reset right now.');
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Reset Password - TikPlat</title>
      </Helmet>
      <Card>
        {token ? (
          <>
            <Title>Reset your password</Title>
            <SubTitle>Enter a new password for your account.</SubTitle>

            {message && <Message $type="success">{message}</Message>}
            {error && <Message $type="error">{error}</Message>}

            <Form onSubmit={handleSubmit}>
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter a new password"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" disabled={loading || !token || !tokenValid || validating}>
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </Form>
          </>
        ) : (
          <>
            <Title>Forgot your password?</Title>
            <SubTitle>Enter your email and we'll send you a reset link.</SubTitle>

            {requestMessage && <Message $type="success">{requestMessage}</Message>}
            {requestError && <Message $type="error">{requestError}</Message>}

            <Form onSubmit={handleRequestReset}>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>
              <Button type="submit" disabled={requestLoading}>
                {requestLoading ? 'Sending...' : 'Send reset link'}
              </Button>
            </Form>
          </>
        )}
      </Card>
    </PageContainer>
  );
};

export default ResetPasswordPage;
